import {
  ANIMATE_TOOL,
  BRIEF_TOOL,
  COPY_AD_TOOL,
  CREATE_WORKSPACE,
  EDIT_ASSET_TOOL,
  EDIT_IMAGE_TOOL,
  EDIT_IMAGE_WITH_PROMPT_TOOL,
  EDIT_SHAPE_TOOL,
  EDIT_TEXT_TOOL,
  EDIT_WORKSPACE,
  EXPAND_TOOL,
  FILL_TOOL,
  GENERATE_TOOL,
  TRANSFER_TOOL,
  UPSCALE_TOOL,
  VARIATION_TOOL,
} from "../../routes.ts";
import type { GenerationType, ModelCompatibleTool } from "./types.ts";

export const FAVORITE_TAG_NAME = "__favorite__";

export const IMAGE_RESOURCE_TYPE = "IMAGE";
export const VIDEO_RESOURCE_TYPE = "VIDEO";

export const RESOURCE_TYPES = [
  IMAGE_RESOURCE_TYPE,
  VIDEO_RESOURCE_TYPE,
] as const;

export const RESOURCE_TYPE_TO_EXTENSION = {
  [IMAGE_RESOURCE_TYPE]: "png",
  [VIDEO_RESOURCE_TYPE]: "mp4",
} as const;

export const TEXT_TO_IMAGE_GENERATION_TYPE = "TEXT_TO_IMAGE";
export const EDIT_IMAGE_WITH_PROMPT_GENERATION_TYPE = "EDIT_IMAGE_WITH_PROMPT";
export const IMAGE_TO_IMAGE_GENERATION_TYPE = "IMAGE_TO_IMAGE";
export const UPSCALER_GENERATION_TYPE = "UPSCALER";
export const GENERATIVE_FILL_GENERATION_TYPE = "GENERATIVE_FILL";
export const STYLE_TRANSFER_GENERATION_TYPE = "STYLE_TRANSFER";
export const BRIEF_TO_IMAGE_GENERATION_TYPE = "BRIEF_TO_IMAGE";
export const EXPAND_GENERATION_TYPE = "EXPAND";
export const ENHANCE_GENERATION_TYPE = "ENHANCE";
export const COPY_AD_GENERATION_TYPE = "COPY_AD";

export const ANIMATE_GENERATION_TYPE = "ANIMATE";

export const GENERATION_TYPES = [
  TEXT_TO_IMAGE_GENERATION_TYPE,
  EDIT_IMAGE_WITH_PROMPT_GENERATION_TYPE,
  IMAGE_TO_IMAGE_GENERATION_TYPE,
  UPSCALER_GENERATION_TYPE,
  GENERATIVE_FILL_GENERATION_TYPE,
  STYLE_TRANSFER_GENERATION_TYPE,
  BRIEF_TO_IMAGE_GENERATION_TYPE,
  EXPAND_GENERATION_TYPE,
  ENHANCE_GENERATION_TYPE,
  ANIMATE_GENERATION_TYPE,
  COPY_AD_GENERATION_TYPE,
] as const;

export const TOOL_TO_GENERATION_TYPE: Record<
  ModelCompatibleTool,
  GenerationType
> = {
  [GENERATE_TOOL]: TEXT_TO_IMAGE_GENERATION_TYPE,
  [BRIEF_TOOL]: BRIEF_TO_IMAGE_GENERATION_TYPE,
  [TRANSFER_TOOL]: STYLE_TRANSFER_GENERATION_TYPE,
  [FILL_TOOL]: GENERATIVE_FILL_GENERATION_TYPE,
  [VARIATION_TOOL]: IMAGE_TO_IMAGE_GENERATION_TYPE,
  [COPY_AD_TOOL]: COPY_AD_GENERATION_TYPE,
} as const;

export const NODE_STATUS = [
  "RUNNING",
  "SUCCEEDED",
  "FAILED",
  "NOT_RUNNING",
] as const;

export const BOARD_WORKSPACES = [CREATE_WORKSPACE, EDIT_WORKSPACE] as const;

export const CREATE_TOOLS = [
  GENERATE_TOOL,
  TRANSFER_TOOL,
  BRIEF_TOOL,
  COPY_AD_TOOL,
] as const;

export const IMAGE_EDITION_TOOLS = [
  EDIT_IMAGE_WITH_PROMPT_TOOL,
  VARIATION_TOOL,
  FILL_TOOL,
  UPSCALE_TOOL,
  EXPAND_TOOL,
  ANIMATE_TOOL,
] as const;

export const DESIGNER_TOOLS = [
  EDIT_TEXT_TOOL,
  EDIT_SHAPE_TOOL,
  EDIT_IMAGE_TOOL,
  EDIT_ASSET_TOOL,
  COPY_AD_TOOL,
] as const;

export const EDIT_TOOLS = [...IMAGE_EDITION_TOOLS, ...DESIGNER_TOOLS] as const;

export const TOOLS = [...CREATE_TOOLS, ...EDIT_TOOLS] as const;

export const MODEL_COMPATIBLE_TOOLS = [
  GENERATE_TOOL,
  BRIEF_TOOL,
  TRANSFER_TOOL,
  FILL_TOOL,
  VARIATION_TOOL,
  COPY_AD_TOOL,
] as const;

export const GENERATION_RATIOS = [
  "r_1_1",
  "r_9_16",
  "r_4_5",
  "r_3_4",
  "r_191_100",
  "r_16_9",
  "r_4_3",
] as const;

export const SCALE_FACTORS = ["2", "4"] as const;

export const EDIT_ASSET_PANEL_TYPE = [
  "INFO",
  "TAGS",
  "DEBUG",
  "HISTORY",
] as const;

export const LORA_GENERATION_TYPE = "LORA";

export const REFRAME_GENERATION_TYPE = "REFRAME";

export const BACKGROUND_REPLACE_GENERATION_TYPE = "BACKGROUND_REPLACE";

export const MODEL_GENERATION_TYPES = [
  LORA_GENERATION_TYPE,
  REFRAME_GENERATION_TYPE,
  BACKGROUND_REPLACE_GENERATION_TYPE,
] as const;

export const BACKGROUND_REPLACE_QUALITY_PRESETS_WITHOUT_CUSTOM = [
  "FAST",
  "HIGH",
] as const;

export const BACKGROUND_REPLACE_QUALITY_PRESETS = [
  ...BACKGROUND_REPLACE_QUALITY_PRESETS_WITHOUT_CUSTOM,
  "CUSTOM",
] as const;

export const OBJECT_RERENDERED_QUALITY_PRESET_NOT_APPLIED = "NOT_APPLIED";

export const OBJECT_RERENDERED_QUALITY_PRESETS = [
  "EXPRESS",
  "STANDARD",
  "PREMIUM",
] as const;

export const BACKGROUND_REPLACEMENT_MODES = [
  "IDEOGRAM_REPLACE_BACKGROUND",
  "FLUX_PIPELINE",
] as const;

export const ZERO_SHOT_GENERATION_TYPE = "ZERO_SHOT";

export const ZERO_SHOT_CONSISTENCY_PRESETS = ["LOW", "HIGH"] as const;
